import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useTheme } from 'styled-components';
import { Formik } from 'formik';
import Button from '../components/Button';
import { Field, FieldInput, FieldLabel } from '../components/Field';
import Loader from '../components/Loader';
import { Modal, ModalName, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { deleteSpaceCache } from '../utilities/cache';
import { SPACE } from '../utilities/queries';
import { DELETE_SPACE } from '../utilities/mutations';
import { useSpaceContext } from '../contexts/space';

const DeleteSpaceModal = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { spaceVars } = useSpaceContext();
  const { loading, data } = useQuery(SPACE, {
    variables: { slug: params.s, ...spaceVars },
  });
  const [deleteSpace] = useMutation(DELETE_SPACE, {
    variables: { slug: params.s },
    update: (cache, { data: { deleteSpace } }) =>
      deleteSpaceCache(params.s, cache, deleteSpace),
  });
  const theme = useTheme();
  const toast = useToast();

  return (
    <Modal onClose={() => navigate(`/s/${params.s}/settings/data`)} isDanger>
      {loading && !data ? (
        <ModalPart>
          <Loader />
        </ModalPart>
      ) : (
        <>
          <ModalName>Delete space</ModalName>
          <Formik
            initialValues={{ name: '' }}
            onSubmit={async (values) => {
              if (values.name !== data.space.name) {
                toast({ children: 'Name is invalid' });
              } else {
                try {
                  await deleteSpace();
                  navigate('/login');
                } catch (error) {
                  toast({ children: error.message });
                }
              }
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <ModalPart>
                  <div
                    style={{
                      color: theme.alias.subtle,
                      lineHeight: '20px',
                      fontWeight: 500,
                    }}
                  >
                    This will instantly delete this space and all of its data
                    forever.
                  </div>
                  <Field>
                    <FieldLabel>
                      Confirm the space name ({data.space.name})
                    </FieldLabel>
                    <FieldInput
                      type="text"
                      name="name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      data-autofocus
                    />
                  </Field>
                </ModalPart>
                <ModalPart>
                  <Button
                    type="button"
                    onClick={() => navigate(`/s/${params.s}/settings/data`)}
                    isExtended
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || values.name !== data.space.name}
                    isDanger
                    isExtended
                  >
                    Delete
                  </Button>
                </ModalPart>
              </form>
            )}
          </Formik>
        </>
      )}
    </Modal>
  );
};

export default DeleteSpaceModal;
